import type { CategoryNode } from '@services/productInventory';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

function useCreateCategorySchema(existingCategories: CategoryNode[] = []) {
  const { t: i18n } = useTranslation('manageProduct');

  return z.object({
    name: z
      .string()
      .max(40, i18n('partsCategories.interactive.nameTooLong'))
      .min(2, i18n('partsCategories.interactive.nameTooShort'))
      .min(1, i18n('partsCategories.interactive.nameRequired'))
      .trim()
      .refine(
        (
          name // Check if name contains an isocolon character
        ) => !name.includes('⁝'),
        {
          message: i18n('partsCategories.interactive.restrictedCharacter', { character: '⁝' }),
        }
      )
      .refine(
        (name) =>
          // Check if name already exists (case insensitive)
          !existingCategories.some((category) => category.name.toLowerCase() === name.toLowerCase()),
        { message: i18n('partsCategories.interactive.nameAlreadyExists') }
      ),
  });
}

export type CategorySchema = z.infer<ReturnType<typeof useCreateCategorySchema>>;

export function useValidateNewCategory(existingCategories?: CategoryNode[]) {
  const categorySchema = useCreateCategorySchema(existingCategories);

  const validateRow = (values: Partial<CategoryNode>): Record<string, string> => {
    const errors: Record<string, string> = {};

    try {
      categorySchema.parse({
        name: values.name || '',
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          const path = err.path[0] as string;
          errors[path] = err.message;
        });
      }
    }

    return errors;
  };

  return { validateRow };
}
